/**
 * Admin Dashboard API Service
 */

import { get } from "../client";

export interface OrdersByStatus {
  pending: number;
  paid: number;
  completed: number;
  cancelled: number;
}

export interface DashboardStats {
  overall: {
    totalRevenue: number;
    totalNetIncome: number;
    totalOrders: number;
    allOrders: number;
    ordersByStatus: OrdersByStatus;
  };
  today: {
    totalVisitors: number;
    checkedIn: number;
  };
  chart: Array<{
    date: string;
    revenue: number;
    volume: number;
  }>;
  topAffiliates: Array<{
    name: string;
    code: string;
    sales: number;
  }>;
}

interface DashboardResponse {
  message?: string;
  data?: any;
}

const emptyStats: DashboardStats = {
  overall: {
    totalRevenue: 0,
    totalNetIncome: 0,
    totalOrders: 0,
    allOrders: 0,
    ordersByStatus: {
      pending: 0,
      paid: 0,
      completed: 0,
      cancelled: 0,
    },
  },
  today: {
    totalVisitors: 0,
    checkedIn: 0,
  },
  chart: [],
  topAffiliates: [],
};

function normalizeDashboardStats(raw: any): DashboardStats {
  if (!raw || typeof raw !== "object") {
    return emptyStats;
  }

  if (raw.overall && raw.today && Array.isArray(raw.chart)) {
    return {
      overall: {
        totalRevenue: Number(raw.overall.totalRevenue || 0),
        totalNetIncome: Number(raw.overall.totalNetIncome || 0),
        totalOrders: Number(raw.overall.totalOrders || raw.overall.allOrders || 0),
        allOrders: Number(raw.overall.allOrders || raw.overall.totalOrders || 0),
        ordersByStatus: {
          pending: Number(raw.overall.ordersByStatus?.pending || 0),
          paid: Number(raw.overall.ordersByStatus?.paid || 0),
          completed: Number(raw.overall.ordersByStatus?.completed || 0),
          cancelled: Number(raw.overall.ordersByStatus?.cancelled || 0),
        },
      },
      today: {
        totalVisitors: Number(raw.today.totalVisitors || 0),
        checkedIn: Number(raw.today.checkedIn || 0),
      },
      chart: raw.chart.map((item: any) => ({
        date: String(item.date || new Date().toISOString().slice(0, 10)),
        revenue: Number(item.revenue || 0),
        volume: Number(item.volume || 0),
      })),
      topAffiliates: Array.isArray(raw.topAffiliates)
        ? raw.topAffiliates.map((item: any) => ({
          name: String(item.name || ""),
          code: String(item.code || ""),
          sales: Number(item.sales || 0),
        }))
        : [],
    };
  }

  if (raw.stats && typeof raw.stats === "object") {
    return {
      ...emptyStats,
      overall: {
        ...emptyStats.overall,
        totalRevenue: Number(raw.stats.totalRevenue || 0),
        totalNetIncome: Number(raw.stats.totalNetIncome || 0),
        totalOrders: Number(raw.stats.totalBookings || 0),
        allOrders: Number(raw.stats.totalBookings || 0),
      },
    };
  }

  return emptyStats;
}

/**
 * Get dashboard statistics
 */
export async function getDashboardStats(): Promise<DashboardStats> {
  const response = await get<DashboardResponse>("/admin/dashboard");
  return normalizeDashboardStats(response.data);
}
